// Variant editor for an order line. Renders inside its own <tr> below
// the main line row in OrderDetailPage. Reads the line's variants from
// the parent's already-fetched detail payload, mutates via PATCH /
// POST / DELETE on /orders/:id/lines/:line_id/variants[/:variant_id].
//
// Backend rolls up `lines.quantity_total` from SUM(variants.quantity)
// on every change (services/orders-builder.ts rollupQuantityTotal), so
// after each mutation we invalidate the order detail query and the
// line-level qty + order totals re-derive themselves.
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { QueryKey } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { SizeBreakdownEditor, type ColourOption } from "./SizeBreakdownEditor";

export interface LineVariant {
  id: string;
  color: string | null;
  size: string | null;
  material: string | null;
  quantity: number;
  unitCost: string | null;
  unitPrice: string | null;
}

interface Props {
  orderId: string;
  lineItemId: string;
  variants: LineVariant[];
  detailQueryKey: QueryKey;
  /** Column count of the parent line table — used for colSpan on the
   *  wrapper <td>. Pass the same number of columns the line row uses. */
  colSpan: number;
  /** Reps can edit while statusStage='quote'. False renders read-only. */
  editable: boolean;
  /** Catalogue colours/sizes for the line's product (colour × size editor). */
  colours?: ColourOption[];
}

export function VariantTableRow(props: Props) {
  // Soft visual nesting: indent the variant table so it reads as a
  // child of the line row.
  return (
    <tr className="bg-cream-50/60">
      <td className="px-4 py-3" />
      <td className="px-4 py-3" colSpan={Math.max(1, props.colSpan - 1)}>
        <VariantTable {...props} />
      </td>
    </tr>
  );
}

function VariantTable(props: Props) {
  const { orderId, lineItemId, variants, detailQueryKey, editable } = props;
  const queryClient = useQueryClient();

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: detailQueryKey });
  };

  const patchMutation = useMutation({
    mutationFn: async (args: {
      variantId: string;
      body: Record<string, unknown>;
    }) =>
      api<{ variant: LineVariant }>(
        `/orders/${orderId}/lines/${lineItemId}/variants/${args.variantId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(args.body),
        },
      ),
    onSuccess: invalidate,
  });

  const removeMutation = useMutation({
    mutationFn: async (variantId: string) =>
      api(
        `/orders/${orderId}/lines/${lineItemId}/variants/${variantId}`,
        { method: "DELETE" },
      ),
    onSuccess: invalidate,
  });

  const [isAdding, setIsAdding] = useState(false);

  const totalQty = variants.reduce((sum, v) => sum + v.quantity, 0);

  return (
    <div className="rounded-md border border-brown-100 bg-white">
      <div className="flex items-center justify-between px-3 py-2">
        <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Variants ({variants.length})
        </span>
        <span className="text-xs text-brown-400">
          Total qty <span className="font-semibold text-brown-500 tabular-nums">{totalQty.toLocaleString()}</span>
        </span>
      </div>
      {variants.length === 0 ? (
        <div className="px-3 pb-3 text-xs text-brown-300">
          No variants yet.
        </div>
      ) : (
        <table className="w-full border-t border-brown-50 text-xs">
          <thead className="border-b border-[#e7e7e4] bg-[#fafaf8] text-left text-[11px] font-medium text-muted">
            <tr>
              <th className="px-3 py-2 font-semibold">Color</th>
              <th className="px-3 py-2 font-semibold">Size</th>
              <th className="px-3 py-2 font-semibold">Material</th>
              <th className="px-3 py-2 text-right font-semibold">Qty</th>
              {editable && <th className="w-10 px-3 py-2" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-brown-50">
            {variants.map((v) => (
              <VariantRow
                key={v.id}
                variant={v}
                editable={editable}
                busy={patchMutation.isPending || removeMutation.isPending}
                onPatchQty={(qty) =>
                  patchMutation.mutate({
                    variantId: v.id,
                    body: { quantity: qty },
                  })
                }
                onRemove={() => removeMutation.mutate(v.id)}
              />
            ))}
          </tbody>
        </table>
      )}

      {editable && (
        <div className="border-t border-brown-50 px-3 py-2">
          {!isAdding ? (
            <button
              type="button"
              onClick={() => setIsAdding(true)}
              className="inline-flex items-center gap-1 text-xs font-semibold text-brown-400 hover:text-brown-500"
            >
              <Plus className="h-3 w-3" /> Colors &amp; sizes
            </button>
          ) : (
            <SizeBreakdownEditor
              orderId={orderId}
              lineItemId={lineItemId}
              variants={variants}
              colours={props.colours ?? []}
              detailQueryKey={detailQueryKey}
              onClose={() => setIsAdding(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}

function VariantRow({
  variant,
  editable,
  busy,
  onPatchQty,
  onRemove,
}: {
  variant: LineVariant;
  editable: boolean;
  busy: boolean;
  onPatchQty: (qty: number) => void;
  onRemove: () => void;
}) {
  const [qty, setQty] = useState(String(variant.quantity));

  // Re-seed when canonical value flips (after another field's mutation).
  if (qty !== String(variant.quantity) && document.activeElement?.tagName !== "INPUT") {
    setQty(String(variant.quantity));
  }

  const commit = (): void => {
    const n = Math.max(0, Math.floor(Number(qty) || 0));
    setQty(String(n));
    if (n !== variant.quantity) onPatchQty(n);
  };

  return (
    <tr>
      <td className="px-3 py-1.5 text-brown-500">{variant.color ?? "—"}</td>
      <td className="px-3 py-1.5 text-brown-500">{variant.size ?? "—"}</td>
      <td className="px-3 py-1.5 text-brown-400">{variant.material ?? ""}</td>
      <td className="px-3 py-1.5 text-right">
        {editable ? (
          <input
            type="number"
            min={0}
            value={qty}
            onFocus={(e) => e.currentTarget.select()}
            onChange={(e) => setQty(e.target.value)}
            onBlur={commit}
            disabled={busy}
            className="w-16 rounded-md border border-brown-100 bg-white px-2 py-1 text-right text-xs tabular-nums text-brown-500 focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-200 disabled:opacity-50"
          />
        ) : (
          <span className="tabular-nums text-brown-500">
            {variant.quantity.toLocaleString()}
          </span>
        )}
      </td>
      {editable && (
        <td className="px-3 py-1.5 text-right">
          <button
            type="button"
            onClick={onRemove}
            disabled={busy}
            aria-label="Remove variant"
            className="text-brown-300 transition-colors hover:text-rust-500 disabled:opacity-40"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </td>
      )}
    </tr>
  );
}
